import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessGuide = () => {
  const processSteps = [
    {
      id: 1,
      title: "Select Process",
      description: "Choose from 500+ SAP Best Practices organized by Line of Business (OTC, P2P, RTR, Plan, EWM)",
      icon: "Search",
      color: "bg-blue-500",
      features: ["Process codes & titles", "Version management", "Scope selection"]
    },
    {
      id: 2,
      title: "Answer Contextual Questions",
      description: "AI-powered questionnaire runner with context panel showing org profile and decision history",
      icon: "MessageSquare",
      color: "bg-purple-500",
      features: ["Smart question bank", "Context engine", "Decision tracking"]
    },
    {
      id: 3,
      title: "Fit/Gap Analysis",
      description: "Kanban-based requirement assessment with status columns from Proposed to Approved",
      icon: "GitCompare",
      color: "bg-accent",
      features: ["Visual kanban board", "Status tracking", "Requirement analysis"]
    },
    {
      id: 4,
      title: "Digital Signoffs",
      description: "Multi-signer approval workflow with role-based access and audit trails for compliance",
      icon: "PenTool",
      color: "bg-green-500",
      features: ["Digital signatures", "Audit trails", "Version control"]
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-6">
            <Icon name="Workflow" size={16} className="mr-2" />
            How It Works
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Four Steps to SAP Excellence
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our streamlined process guides you from requirements gathering through final approval, 
            ensuring comprehensive documentation and stakeholder alignment.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid lg:grid-cols-4 gap-8">
          {processSteps?.map((step, index) => (
            <div key={step?.id} className="relative">
              {/* Connection Line */}
              {index < processSteps?.length - 1 && (
                <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-border to-transparent z-0"></div>
              )}
              
              {/* Step Card */}
              <div className="relative bg-card border border-border rounded-xl p-6 hover-lift shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-300 z-10">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${step?.color} rounded-lg flex items-center justify-center`}>
                    <Icon name={step?.icon} size={24} className="text-white" />
                  </div>
                  <div className="text-2xl font-bold text-muted-foreground/30">
                    {step?.id?.toString()?.padStart(2, '0')}
                  </div>
                </div>

                {/* Step Content */}
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {step?.title}
                </h3>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {step?.description}
                </p>

                {/* Features List */}
                <ul className="space-y-2">
                  {step?.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                      <Icon name="Check" size={16} className="text-accent mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Process Flow Visualization */}
        <div className="mt-16 p-8 bg-muted/50 rounded-2xl">
          <div className="flex items-center justify-center space-x-4 overflow-x-auto">
            {processSteps?.map((step, index) => (
              <React.Fragment key={step?.id}>
                <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                  <div className={`w-8 h-8 ${step?.color} rounded-full flex items-center justify-center mb-2`}>
                    <Icon name={step?.icon} size={16} className="text-white" />
                  </div>
                  <span className="text-xs font-medium text-foreground text-center">
                    {step?.title}
                  </span>
                </div>
                
                {index < processSteps?.length - 1 && (
                  <Icon name="ArrowRight" size={20} className="text-muted-foreground flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessGuide;